/**
 * Tenant roles, mirrored from the backend.
 *
 * The source of truth is `functions/src/lib/capabilities.ts` plus the CHECK
 * constraint on `user_profiles.role` (migration 215). This file exists because
 * the frontend cannot import from `functions/`; keep the two in step.
 *
 * The frontend uses these for UX only — which nav items to draw, where to send
 * someone after login, whether to show the maintenance banner or the
 * maintenance page. **None of it is a security boundary.** Every one of these
 * decisions is enforced again server-side by `adminMiddleware` and the
 * capability checks behind it, and it has to stay that way: a role check in a
 * bundle a visitor downloads is a hint, not a gate.
 */

export type TenantRole = 'user' | 'admin' | 'designer';

/** Roles that get the admin shell at all. */
export const ADMIN_SHELL_ROLES: readonly string[] = ['admin', 'designer'];

export function canAccessAdminShell(role: string | null | undefined): boolean {
  return !!role && ADMIN_SHELL_ROLES.includes(role);
}

export function isFullAdmin(role: string | null | undefined): boolean {
  return role === 'admin';
}

export function isDesigner(role: string | null | undefined): boolean {
  return role === 'designer';
}

/**
 * Roles that may view a site while it shows the maintenance / "Coming Soon"
 * page. Must match MAINTENANCE_BYPASS_ROLES in the backend — if the frontend
 * and the cookie-issuing middleware disagree, the failure is intermittent and
 * looks like a caching bug.
 *
 * Designers are included deliberately: pre-launch tenants sit behind "Coming
 * Soon" for weeks and are exactly the ones needing design work, and the Design
 * Sandbox previews by embedding the real storefront in an iframe.
 */
export const MAINTENANCE_BYPASS_ROLES: readonly string[] = ['admin', 'designer'];

export function canBypassMaintenance(role: string | null | undefined): boolean {
  return !!role && MAINTENANCE_BYPASS_ROLES.includes(role);
}
